import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getErrorMessage } from '@/lib/errors';

import { updatePassword } from '../api';
import { useAuth } from '../auth-context';
import { AuthLayout } from '../components/auth-layout';
import { FormAlert } from '../components/form-alert';
import { AuthSplash } from '../components/full-page-state';
import { resetPasswordSchema, type ResetPasswordValues } from '../schemas';

/** Landing page of the recovery email link (session is established by Supabase). */
export function ResetPasswordPage() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  if (status === 'loading') return <AuthSplash label="Validando enlace…" />;

  if (status === 'unauthenticated') {
    return (
      <AuthLayout title="Enlace no válido">
        <FormAlert>
          El enlace de recuperación es inválido o ya expiró. Solicita uno nuevo.
        </FormAlert>
        <Button asChild className="mt-6 w-full" variant="secondary">
          <Link to="/recuperar-contrasena">Solicitar un nuevo enlace</Link>
        </Button>
      </AuthLayout>
    );
  }

  const onSubmit = handleSubmit(async ({ password }) => {
    setFormError(null);
    try {
      await updatePassword(password);
      toast.success('Contraseña actualizada.');
      void navigate('/dashboard', { replace: true });
    } catch (error) {
      setFormError(getErrorMessage(error));
    }
  });

  return (
    <AuthLayout title="Define tu nueva contraseña" description="Usa al menos 8 caracteres.">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <FormAlert>{formError}</FormAlert>}
        <FormField label="Nueva contraseña" error={errors.password?.message} required>
          {(control) => (
            <Input
              {...control}
              {...register('password')}
              type="password"
              autoComplete="new-password"
            />
          )}
        </FormField>
        <FormField label="Confirmar contraseña" error={errors.confirmPassword?.message} required>
          {(control) => (
            <Input
              {...control}
              {...register('confirmPassword')}
              type="password"
              autoComplete="new-password"
            />
          )}
        </FormField>
        <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
          Guardar contraseña
        </Button>
      </form>
    </AuthLayout>
  );
}
